import React, { useState, useEffect, Fragment } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import Pagination from "react-pagination-js";
import PaginationTextView from "../../components/common/PaginationTextView";
import swal from "sweetalert";
import FormControl from "../../components/common/FormControl";

// Helper functions for date formatting
const formatDate = (str) => {
  if (!str) return "—";
  const d = new Date(str);
  if (isNaN(d)) return "—";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ").filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

export default function OrderTrackingList() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // "" represents "All"
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [statusCounts, setStatusCounts] = useState({
    all: 0,
    pending: 0,
    shipped: 0,
    delivered: 0,
  });

  // Modals for editing shipping date and tracking url
  const [activeOrder, setActiveOrder] = useState(null);
  const [isDateModalOpen, setIsDateModalOpen] = useState(false);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [shipDateVal, setShipDateVal] = useState("");
  const [trackingUrlVal, setTrackingUrlVal] = useState("");
  const [submittingModal, setSubmittingModal] = useState(false);

  const limit = 10;

  // Search input debouncer
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 600);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch orders and calculate status counts
  const fetchOrders = async () => {
    setLoading(true);
    loader(true);
    try {
      const params = {
        page: currentPage,
        limit: limit,
        search: debouncedSearch,
      };
      if (statusFilter) {
        params.status = statusFilter.toLowerCase();
      }

      const res = await ApiClient.get("order-tracking/admin/list", params);
      if (res?.success) {
        setOrders(res?.data?.data || []);
        setTotalCount(
          res?.data?.total || (res?.data?.data ? res?.data?.data.length : 0),
        );
        fetchCountsFallback();
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
    } finally {
      setLoading(false);
      loader(false);
    }
  };

  // Fallback to fetch counts for tabs
  const fetchCountsFallback = async () => {
    try {
      const res = await ApiClient.get("order-tracking/admin/list", {
        limit: 100000,
      });
      if (res?.success && Array.isArray(res?.data?.data)) {
        const allData = res?.data?.data;
        const counts = {
          all: allData.length,
          pending: allData.filter((o) => o.status?.toLowerCase() === "pending")
            .length,
          shipped: allData.filter((o) => o.status?.toLowerCase() === "shipped")
            .length,
          delivered: allData.filter(
            (o) => o.status?.toLowerCase() === "delivered",
          ).length,
        };
        setStatusCounts(counts);
      }
    } catch (e) {
      console.error("Error fetching status counts fallback:", e);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [currentPage, statusFilter, debouncedSearch]);

  // Handle status update change
  const handleStatusChange = async (order, newStatus) => {
    const orderId = order._id || order.id;

    let sendInvoiceEmail = false;

    if (
      newStatus.toLowerCase() === "shipped" &&
      order?.invoiceDetails?.status === "due"
    ) {
      const sendInvoice = await swal({
        title: "Send Partial Invoice?",
        text: "This order has an outstanding invoice. Would you like to email the partial invoice to the customer before marking the order as shipped?",
        icon: "info",
        buttons: {
          no: {
            text: "No",
            value: false,
          },
          yes: {
            text: "Yes",
            value: true,
          },
        },
      });

      sendInvoiceEmail = sendInvoice;
    }

    swal({
      title: "Update Status?",
      text: `Are you sure you want to change order status to "${newStatus}"?`,
      icon: "warning",
      buttons: ["Cancel", "Confirm"],
      dangerMode: true,
    }).then(async (confirmed) => {
      if (!confirmed) return;

      loader(true);

      try {
        const res = await ApiClient.put("order-tracking/status/change", {
          id: orderId,
          status: newStatus.toLowerCase(),
          sendInvoiceEmail,
        });

        if (res?.success) {
          swal({
            title: "Success",
            text: "Order status updated successfully.",
            icon: "success",
          });

          fetchOrders();
        } else {
          swal({
            title: "Error",
            text: res?.message || "Failed to update order status.",
            icon: "error",
          });
        }
      } catch (err) {
        console.error("Error updating status:", err);

        swal({
          title: "Error",
          text: "An error occurred while updating the status.",
          icon: "error",
        });
      } finally {
        loader(false);
      }
    });
  };

  // Modal open helpers
  const handleOpenDateModal = (order) => {
    setActiveOrder(order);
    setShipDateVal(order.shipDate || order.shippingDate || "");
    setIsDateModalOpen(true);
  };

  const handleOpenTrackingModal = (order) => {
    setActiveOrder(order);
    setTrackingUrlVal(order.trackingUrl || "");
    setIsTrackingModalOpen(true);
  };

  // Submit shippers/tracking info separately
  const handleDateSubmit = async (e) => {
    e.preventDefault();
    setSubmittingModal(true);
    loader(true);
    try {
      const res = await ApiClient.put("order-tracking/update", {
        id: activeOrder._id || activeOrder.id,
        shipDate: shipDateVal,
      });
      if (res?.success) {
        swal("Success", "Shipping date updated successfully.", "success");
        setIsDateModalOpen(false);
        fetchOrders();
      } else {
        swal(
          "Error",
          res?.message || "Failed to update shipping date.",
          "error",
        );
      }
    } catch (err) {
      console.error(err);
      swal("Error", "An error occurred.", "error");
    } finally {
      setSubmittingModal(false);
      loader(false);
    }
  };

  const handleTrackingSubmit = async (e) => {
    e.preventDefault();
    setSubmittingModal(true);
    loader(true);
    try {
      const res = await ApiClient.put("order-tracking/update", {
        id: activeOrder._id || activeOrder.id,
        trackingUrl: trackingUrlVal,
      });
      if (res?.success) {
        swal("Success", "Tracking URL updated successfully.", "success");
        setIsTrackingModalOpen(false);
        fetchOrders();
      } else {
        swal(
          "Error",
          res?.message || "Failed to update tracking URL.",
          "error",
        );
      }
    } catch (err) {
      console.error(err);
      swal("Error", "An error occurred.", "error");
    } finally {
      setSubmittingModal(false);
      loader(false);
    }
  };

  // Status Badge styles helper
  const getStatusBadgeClass = (status) => {
    const val = (status || "").toLowerCase();
    if (val === "pending") return "bg-amber-50 text-amber-700 border-amber-200";
    if (val === "shipped") return "bg-blue-50 text-blue-700 border-blue-200";
    if (val === "delivered")
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    return "bg-gray-50 text-gray-700 border-gray-200";
  };

  const getStatusBadgeDot = (status) => {
    const val = (status || "").toLowerCase();
    if (val === "pending") return "bg-amber-500";
    if (val === "shipped") return "bg-blue-500";
    if (val === "delivered") return "bg-emerald-500";
    return "bg-gray-500";
  };

  const STATUS_TABS = [
    { label: "All", value: "", count: statusCounts.all },
    { label: "Pending", value: "pending", count: statusCounts.pending },
    { label: "Shipped", value: "shipped", count: statusCounts.shipped },
    { label: "Delivered", value: "delivered", count: statusCounts.delivered },
  ];

  return (
    <div className="max-xl:container xl:max-w-[1280px]  mx-auto">
      {/* Page Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div className="flex flex-col gap-1">
          <h2 className="text-gray-900 text-xl sm:text-2xl font-semibold">
            Order Tracking
          </h2>
          <p className="text-sm sm:text-md text-gray-600">
            Monitor shipping statuses and customer orders
          </p>
        </div>
        <button
          type="button"
          onClick={fetchOrders}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-xl bg-white hover:bg-gray-50 text-gray-700 text-sm font-medium transition-colors shadow-sm"
        >
          Refresh
        </button>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {[
          {
            label: "Total Orders",
            value: statusCounts.all,
            bg: "bg-[#115B9D17]",
            textColor: "text-[#115B9D]",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                />
              </svg>
            ),
          },
          {
            label: "Pending Orders",
            value: statusCounts.pending,
            bg: "bg-amber-50",
            textColor: "text-amber-600",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ),
          },
          {
            label: "Shipped Orders",
            value: statusCounts.shipped,
            bg: "bg-blue-50",
            textColor: "text-blue-600",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0"
                />
              </svg>
            ),
          },
          {
            label: "Delivered Orders",
            value: statusCounts.delivered,
            bg: "bg-emerald-50",
            textColor: "text-emerald-600",
            icon: (
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            ),
          },
        ].map((card, idx) => (
          <div
            key={idx}
            className="border border-gray-200 rounded-2xl bg-white px-5 py-4 flex items-center gap-4 shadow-sm"
          >
            <div
              className={`w-10 h-10 rounded-xl ${card.bg} ${card.textColor} flex items-center justify-center shrink-0`}
            >
              {card.icon}
            </div>
            <div>
              <p className="text-xs text-gray-600 font-medium">{card.label}</p>
              <p className="text-xl font-bold text-gray-900 mt-0.5">
                {card.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters and Search */}
      <div className="w-full border rounded-2xl bg-white p-5 lg:p-8 shadow-sm">
        <div className="flex justify-between flex-wrap gap-4 mb-6">
          {/* Status Tabs */}
          <div className="max-sm:w-full w-full sm:w-fit overflow-auto flex items-center gap-1 bg-gray-100 rounded-xl max-lg:[scrollbar-width:none] max-lg:[-ms-overflow-style:none] max-lg:[&::-webkit-scrollbar]:hidden p-1.5">
            {STATUS_TABS.map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => {
                  setCurrentPage(1);
                  setStatusFilter(tab.value);
                }}
                className={`whitespace-nowrap px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${statusFilter === tab.value
                  ? "bg-white text-gray-900 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
                  }`}
              >
                {tab.label}
                <span
                  className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${statusFilter === tab.value
                    ? "bg-gray-100 text-gray-600"
                    : "bg-gray-200/70 text-gray-600"
                    }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search Input */}
          <div className="relative w-full sm:w-[350px]">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-600"
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="8" />
              <path d="m21 21-4.3-4.3" />
            </svg>
            <input
              type="text"
              placeholder="Search by ID, name, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all"
            />
          </div>
        </div>

        {/* Table Container */}
        <div className="hidden md:block overflow-hidden border border-gray-200 rounded-xl">
          <table className="w-full table-fixed">
            <colgroup>
              <col style={{ width: "9%" }} />  {/* Actions */}
              <col style={{ width: "7%" }} />  {/* Invoice */}
              <col style={{ width: "8%" }} />  {/* Invoice Status */}
              <col style={{ width: "18%" }} /> {/* Customer + Email */}
              <col style={{ width: "13%" }} /> {/* Status */}
              <col style={{ width: "11%" }} /> {/* Shipping Date */}
              <col style={{ width: "8%" }} />  {/* Total Tax */}
              <col style={{ width: "9%" }} />  {/* Total Amount */}
              <col style={{ width: "10%" }} /> {/* Created Date */}
            </colgroup>
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {[
                  "Actions",
                  "Invoice",
                  "Invoice Status",
                  "Customer",
                  "Status",
                  "Shipping Date",
                  "Total Tax",
                  "Total Amount",
                  "Created Date",
                ].map((header) => (
                  <th
                    key={header}
                    className="text-left py-2.5 px-2 font-semibold text-xs text-gray-600 whitespace-normal leading-tight align-bottom"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="text-center py-16">
                    <div className="flex flex-col items-center gap-3">
                      <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center text-gray-600">
                        <svg
                          className="w-6 h-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="1.5"
                            d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                          />
                        </svg>
                      </div>
                      <p className="text-sm text-gray-500 font-medium">
                        No tracking orders found
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const status = order.status || "pending";
                  const isDelivered = status.toLowerCase() === "delivered";
                  const isShippedOrDelivered =
                    status.toLowerCase() === "shipped" || isDelivered;
                  return (
                    <tr
                      key={order._id || order.id}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      {/* Actions */}
                      <td className="py-2.5 px-2">
                        <div className="flex items-center gap-0.5">
                          <button
                            type="button"
                            disabled={isShippedOrDelivered}
                            onClick={() => handleOpenDateModal(order)}
                            title={
                              isShippedOrDelivered
                                ? "Shipping date cannot be changed once shipped or delivered"
                                : "Edit Shipping Date"
                            }
                            className={`w-7 h-7 rounded-lg flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0 ${isShippedOrDelivered
                              ? "opacity-30 cursor-not-allowed"
                              : "hover:bg-gray-100"
                              }`}
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </button>

                          <button
                            type="button"
                            disabled={isDelivered}
                            onClick={() => handleOpenTrackingModal(order)}
                            title={
                              isDelivered
                                ? "Tracking details cannot be changed once delivered"
                                : "Edit Tracking URL"
                            }
                            className={`w-7 h-7 rounded-lg flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0 ${isDelivered
                              ? "opacity-30 cursor-not-allowed"
                              : "hover:bg-gray-100"
                              }`}
                          >
                            <svg
                              className="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-5 7h3m-3 4h3m-6-4h.01M9 16h.01"
                              />
                            </svg>
                          </button>

                          <button
                            type="button"
                            onClick={() =>
                              navigate(
                                `/order/tracking/detail/${order._id || order.id}`,
                              )
                            }
                            title="View Details"
                            className="w-7 h-7 rounded-lg hover:bg-gray-100 flex items-center justify-center text-gray-600 hover:text-[#115B9D] transition-colors shrink-0"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="14"
                              height="14"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                              strokeWidth="2"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                          </button>
                        </div>
                      </td>

                      {/* Order ID */}
                      <td className="py-2.5 px-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-lg bg-[#115B9D]/8 border border-[#115B9D]/15 text-[11px] font-semibold text-[#115B9D] truncate max-w-full">
                          {order.invoiceNumber || "--"}
                        </span>
                      </td>

                      {/* Invoice Status */}
                      <td className="py-2.5 px-2">
                        <span className="text-xs capitalize text-gray-600 truncate block">
                          {order.invoiceStatus == "partial_paid"
                            ? "Partial Paid"
                            : order.invoiceStatus || "--"}
                        </span>
                      </td>

                      {/* Customer Name + Email — combined cell */}
                      <td className="py-2.5 px-2 overflow-hidden">
                        <div className="flex items-start gap-2 min-w-0">
                          <div className="w-7 h-7 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-[9px] font-semibold text-[#115B9D]">
                              {getInitials(
                                order.customerName || order.customer?.name || "",
                              )}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-gray-800 capitalize truncate">
                              {order.customerName || order.customer?.name || "—"}
                            </p>
                            <p
                              className="text-[11px] text-gray-600 break-all leading-tight"
                              title={order.email || order.userId?.email || ""}
                            >
                              {order.email || order.userId?.email || "—"}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Status — badge and dropdown stacked vertically now */}
                      <td className="py-2.5 px-2">
                        <div className="flex flex-col items-start gap-1.5">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[10px] font-medium border whitespace-nowrap ${getStatusBadgeClass(status)}`}
                          >
                            <span
                              className={`w-1.5 h-1.5 rounded-full shrink-0 ${getStatusBadgeDot(status)}`}
                            />
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>

                          {/* Quick Status Select */}
                          {isDelivered ? (
                            <span className="text-[10px] text-gray-600 flex items-center gap-1 font-medium bg-gray-50 border border-gray-100 rounded px-1.5 py-0.5 whitespace-nowrap">
                              <svg
                                className="w-3 h-3 text-gray-600 shrink-0"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path
                                  fillRule="evenodd"
                                  d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                              Locked
                            </span>
                          ) : (
                            <div className="select_control w-full">
                              <FormControl
                                placeholder="Select Status"
                                theme="search"
                                type="select"
                                options={[
                                  { name: "Pending", id: "pending" },
                                  { name: "Shipped", id: "shipped" },
                                  { name: "Delivered", id: "delivered" },
                                ]}
                                value={status.toLowerCase()}
                                onChange={(e) => handleStatusChange(order, e)}
                              />
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Shipping Date */}
                      <td className="py-2.5 px-2 text-xs text-gray-600 whitespace-nowrap">
                        {formatDate(order.shipDate || order.shippingDate)}
                      </td>

                      {/* Total Tax */}
                      <td className="py-2.5 px-2">
                        <span className="text-xs capitalize text-gray-600 truncate block">
                          ${order.invoiceDetails?.totalTax || "0"}
                        </span>
                      </td>

                      {/* Total Amount */}
                      <td className="py-2.5 px-2">
                        <span className="text-xs capitalize text-gray-600 truncate block">
                          ${order.invoiceDetails?.totalAmount || "0"}
                        </span>
                      </td>

                      {/* Created Date */}
                      <td className="py-2.5 px-2 text-xs text-gray-600 whitespace-nowrap">
                        {formatDate(order.createdAt || order.createdDate)}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ================= Mobile Order Cards ================= */}

        <div className="block md:hidden space-y-4">

          {orders.length === 0 ? (

            <div className="bg-white border border-gray-200 rounded-2xl py-12 px-6 text-center">

              <div className="w-14 h-14 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-4">

                <svg
                  className="w-6 h-6 text-gray-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="1.5"
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>

              </div>

              <p className="text-sm text-gray-500 font-medium">
                No tracking orders found
              </p>

            </div>

          ) : (

            orders.map((order) => {

              const status = order.status || "pending";

              const isDelivered =
                status.toLowerCase() === "delivered";

              const isShippedOrDelivered =
                status.toLowerCase() === "shipped" ||
                isDelivered;

              return (

                <div
                  key={order._id || order.id}
                  className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden"
                >

                  {/* Header */}

                  <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">

                    <div>

                      <p className="text-xs text-gray-500">
                        Invoice
                      </p>

                      <p className="font-semibold text-[#115B9D]">
                        {order.invoiceNumber || "--"}
                      </p>

                    </div>

                    <span
                      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadgeClass(
                        status
                      )}`}
                    >
                      <span
                        className={`w-2 h-2 rounded-full ${getStatusBadgeDot(
                          status
                        )}`}
                      />

                      {status.charAt(0).toUpperCase() +
                        status.slice(1)}

                    </span>

                  </div>

                  {/* Body */}

                  <div className="p-4 space-y-4">

                    {/* Customer */}

                    <div className="flex items-center gap-3">

                      <div className="w-11 h-11 rounded-xl bg-[#115B9D17] border border-[#115B9D]/10 flex items-center justify-center">

                        <span className="text-xs font-semibold text-[#115B9D]">

                          {getInitials(
                            order.customerName ||
                            order.customer?.name ||
                            ""
                          )}

                        </span>

                      </div>

                      <div className="flex-1">

                        <p className="font-semibold text-gray-800 capitalize">

                          {order.customerName ||
                            order.customer?.name ||
                            "—"}

                        </p>

                        <p className="text-xs text-gray-500 break-all">

                          {order.email ||
                            order.userId?.email ||
                            "—"}

                        </p>

                      </div>

                    </div>

                    {/* Invoice Status */}

                    <div className="grid grid-cols-2 gap-3">

                      <div className="border border-gray-200 rounded-xl p-3">

                        <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                          Invoice Status
                        </p>

                        <p className="text-sm font-semibold text-gray-800">

                          {order.invoiceStatus === "partial_paid"
                            ? "Partial Paid"
                            : order.invoiceStatus || "--"}

                        </p>

                      </div>

                      <div className="border border-gray-200 rounded-xl p-3">

                        <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                          Shipping Date
                        </p>

                        <p className="text-sm font-semibold text-gray-800">

                          {formatDate(
                            order.shipDate ||
                            order.shippingDate
                          )}

                        </p>

                      </div>

                    </div>

                    {/* Tax & Amount */}

                    <div className="grid grid-cols-2 gap-3">

                      <div className="border border-gray-200 rounded-xl p-3">

                        <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                          Total Tax
                        </p>

                        <p className="text-sm font-semibold text-gray-800">

                          $
                          {order.invoiceDetails?.totalTax || "0"}

                        </p>

                      </div>

                      <div className="border border-gray-200 rounded-xl p-3">

                        <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                          Total Amount
                        </p>

                        <p className="text-sm font-bold text-[#115B9D]">

                          $
                          {order.invoiceDetails?.totalAmount || "0"}

                        </p>

                      </div>

                    </div>

                    {/* Created Date */}

                    <div className="border border-gray-200 rounded-xl p-3">

                      <p className="text-[11px] uppercase text-gray-500 font-medium mb-1">
                        Created Date
                      </p>

                      <p className="text-sm font-semibold text-gray-800">

                        {formatDate(
                          order.createdAt ||
                          order.createdDate
                        )}

                      </p>

                    </div>

                    {/* Status */}

                    <div className="border border-gray-200 rounded-xl p-3">

                      <p className="text-[11px] uppercase text-gray-500 font-medium mb-3">
                        Order Status
                      </p>

                      <div className="space-y-3">

                        <span
                          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium border ${getStatusBadgeClass(
                            status
                          )}`}
                        >
                          <span
                            className={`w-2 h-2 rounded-full ${getStatusBadgeDot(
                              status
                            )}`}
                          />

                          {status.charAt(0).toUpperCase() +
                            status.slice(1)}

                        </span>

                        {isDelivered ? (

                          <div className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">

                            <svg
                              className="w-4 h-4 text-gray-500"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
                                clipRule="evenodd"
                              />
                            </svg>

                            <span className="text-xs font-medium text-gray-600">
                              Locked
                            </span>

                          </div>

                        ) : (

                          <div className="select_control">

                            <FormControl
                              placeholder="Select Status"
                              theme="search"
                              type="select"
                              options={[
                                {
                                  name: "Pending",
                                  id: "pending",
                                },
                                {
                                  name: "Shipped",
                                  id: "shipped",
                                },
                                {
                                  name: "Delivered",
                                  id: "delivered",
                                },
                              ]}
                              value={status.toLowerCase()}
                              onChange={(e) =>
                                handleStatusChange(order, e)
                              }
                            />

                          </div>

                        )}

                      </div>

                    </div>

                    {/* Actions */}

                    <div className="grid grid-cols-3 gap-3">

                      <button
                        type="button"
                        disabled={isShippedOrDelivered}
                        onClick={() =>
                          handleOpenDateModal(order)
                        }
                        className={`h-11 rounded-xl border text-sm font-medium transition-all ${isShippedOrDelivered
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-gray-50"
                          }`}
                      >
                        Ship Date
                      </button>

                      <button
                        type="button"
                        disabled={isDelivered}
                        onClick={() =>
                          handleOpenTrackingModal(order)
                        }
                        className={`h-11 rounded-xl border text-sm font-medium transition-all ${isDelivered
                          ? "opacity-40 cursor-not-allowed"
                          : "hover:bg-gray-50"
                          }`}
                      >
                        Tracking
                      </button>

                      <button
                        type="button"
                        onClick={() =>
                          navigate(
                            `/order/tracking/detail/${order._id || order.id
                            }`
                          )
                        }
                        className="h-11 rounded-xl bg-[#115B9D] text-white text-sm font-medium hover:bg-[#0d4d87] transition-all"
                      >
                        View
                      </button>

                    </div>

                  </div>

                </div>

              );

            })

          )}

        </div>

        {/* Responsive Pagination */}

        {totalCount > limit && (

          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-5 pt-5 border-t border-gray-100">

            <p className="text-sm text-gray-600 text-center md:text-left">

              {PaginationTextView(
                currentPage,
                limit,
                totalCount
              )}

            </p>

            <div className="flex justify-center md:justify-end">

              <Pagination
                currentPage={currentPage}
                totalSize={totalCount}
                sizePerPage={limit}
                changeCurrentPage={(page) =>
                  setCurrentPage(page)
                }
              />

            </div>

          </div>

        )}

        {/* Pagination */}
        {/* {totalCount > limit && (
          <div className="flex items-center justify-between mt-5 pt-5 border-t border-gray-100 flex-wrap gap-3">
            <p className="text-gray-600 text-sm">
              {PaginationTextView(currentPage, limit, totalCount)}
            </p>
            <Pagination
              currentPage={currentPage}
              totalSize={totalCount}
              sizePerPage={limit}
              changeCurrentPage={(page) => setCurrentPage(page)}
            />
          </div>
        )} */}
      </div>

      {/* Edit Shipping Date Modal */}
      <Transition appear show={isDateModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setIsDateModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-3 mb-4"
                  >
                    Edit Shipped Date
                  </Dialog.Title>

                  <form onSubmit={handleDateSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1.5">
                        Shipping Date
                      </label>
                      <input
                        type="date"
                        value={shipDateVal}
                        onChange={(e) => setShipDateVal(e.target.value)}
                        className="h-10 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                        required
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-6">
                      <button
                        type="button"
                        onClick={() => setIsDateModalOpen(false)}
                        className="px-4 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={submittingModal}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-semibold bg-[#115B9D] hover:opacity-90 rounded-xl text-white transition-colors"
                        disabled={submittingModal}
                      >
                        {submittingModal ? "Saving..." : "Save Date"}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Tracking URL Modal */}
      <Transition appear show={isTrackingModalOpen} as={Fragment}>
        <Dialog
          as="div"
          className="relative z-[999]"
          onClose={() => setIsTrackingModalOpen(false)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-xl transition-all">
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-bold text-gray-900 border-b border-gray-100 pb-3 mb-4"
                  >
                    Edit Tracking URL
                  </Dialog.Title>

                  <form onSubmit={handleTrackingSubmit} className="space-y-4">
                    <div>
                      <label className="block text-sm text-gray-700 font-medium mb-1.5">
                        Tracking URL
                      </label>
                      <input
                        type="url"
                        placeholder="https://example.com/tracking"
                        value={trackingUrlVal}
                        onChange={(e) => setTrackingUrlVal(e.target.value)}
                        className="h-10 w-full border border-gray-200 rounded-xl px-3 py-2 text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#115B9D]/30 focus:border-[#115B9D] transition-all bg-white"
                        required
                      />
                    </div>

                    <div className="flex justify-end gap-3 pt-3 border-t border-gray-100 mt-6">
                      <button
                        type="button"
                        onClick={() => setIsTrackingModalOpen(false)}
                        className="px-4 py-2 text-sm font-semibold border border-gray-200 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors"
                        disabled={submittingModal}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="px-4 py-2 text-sm font-semibold bg-[#115B9D] hover:opacity-90 rounded-xl text-white transition-colors"
                        disabled={submittingModal}
                      >
                        {submittingModal ? "Saving..." : "Save Tracking URL"}
                      </button>
                    </div>
                  </form>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}
